import { useCallback, useState } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { API_BASE_URL } from '@mmef/shared/api';
import { originPlaceResolved, originResolved } from './filtersSlice';

/**
 * Asking the browser where the reader is, and turning the answer into a search origin.
 *
 * <p>Its own hook because two things now ask: the "Near me" option in the Where list, and the
 * prompt that appears when distances are being measured from a town centre. Two copies of a
 * permission request is how they drift — one asking with a timeout and one without, one clearing
 * the previous error and one leaving it on screen under a successful result.
 *
 * <p><b>Never called on mount.</b> An unprompted location dialog is the kind of thing people deny
 * once and never revisit, and a denial is remembered by the browser — so asking at the wrong
 * moment doesn't just fail, it removes the option permanently.
 */
export function useLocationOrigin() {
  const dispatch = useAppDispatch();
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setError('This browser can’t share your location. Choose a town instead.');
      return;
    }

    setLocating(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        dispatch(originResolved({ lat, lng, label: 'your location', source: 'geo' }));
        setLocating(false);

        /*
         * Name the area, separately and afterwards.
         *
         * <p>The search is already usable on the coordinates alone, so this is deliberately not
         * awaited and never blocks: it exists so the reader can see the browser put them somewhere
         * sensible before trusting the distances. Wifi positioning is regularly a few miles out,
         * and a distance column measured from the wrong town is wrong in a way the numbers by
         * themselves cannot show.
         *
         * <p>A failure is swallowed on purpose. Losing the label costs a reassurance; showing an
         * error about it would suggest the search itself had gone wrong, which it hasn't.
         */
        fetch(`${API_BASE_URL}/api/place?lat=${lat}&lng=${lng}`)
          .then((response) => (response.ok ? response.json() : null))
          .then((body) => {
            if (body?.place) dispatch(originPlaceResolved({ lat, lng, place: body.place }));
          })
          .catch(() => {});
      },
      (failure) => {
        // Told apart because the reader can act on one and not the other: a refusal is theirs to
        // reverse in browser settings, while a timeout is worth simply trying again.
        setError(
          failure.code === failure.PERMISSION_DENIED
            ? 'You’ve blocked location for this site. Allow it in your browser, or pick a town.'
            : 'We couldn’t work out where you are. Try again, or pick a town.',
        );
        setLocating(false);
      },
      { timeout: 10_000 },
    );
  }, [dispatch]);

  return { request, locating, error, clearError: () => setError(null) };
}
